import { StateMachine } from "@/core/arch/state_machine";
import { ROUTES, merchantPath, merchantTabPath } from "@/routes";
import { searchDocs } from "./matcher";
import { isMissingSearchIndex } from "./search_index_repository";
import { classifyQuery } from "./query_classifier";
import type { SearchIntent } from "./query_classifier";
import type { LiveMatch, SearchRepository } from "./search_repository";
import type { SearchIndexArtifact } from "./types";

/** Index matches shown in the palette. Beyond this, refine the query. */
const MAX_INDEX_RESULTS = 8;

/** One selectable row in the palette. */
export interface SearchResultItem {
  id: string;
  title: string;
  subtitle?: string;
  path: string;
}

export interface UniversalSearchState {
  query: string;
  status: "idle" | "searching" | "ready";
  /** True when a lookup failed, so empty results are not reported as "none". */
  failed: boolean;
  /** Records resolved by exact, live lookup. Always ranked first. */
  exact: SearchResultItem[];
  /** Fuzzy matches from the prebuilt index. */
  merchants: SearchResultItem[];
}

/** Results from one source, plus whether that source errored. */
interface ProbeOutcome {
  items: SearchResultItem[];
  failed: boolean;
}

/** The subset of the index repository the palette needs. */
export interface SearchIndexSource {
  load: () => Promise<SearchIndexArtifact>;
}

function pathFor(match: LiveMatch): string {
  switch (match.kind) {
    case "transaction":
      return ROUTES.TRANSACTION_DETAIL.replace(
        ":businessId",
        match.businessId,
      ).replace(":txId", match.id);
    case "invoice":
      return merchantTabPath(match.businessId, "invoices");
    case "merchant":
      return merchantPath(match.businessId);
  }
}

/**
 * Drives the command palette.
 *
 * Free text is matched against the prebuilt index; anything with an
 * identifiable shape is additionally resolved by a live, indexed lookup. Both
 * run for the same keystroke, which is what lets the palette find a merchant
 * created since the last index build: the artifact does not have them, but
 * their email or Stripe id still resolves against a live GSI.
 */
export class UniversalSearchBloc extends StateMachine<UniversalSearchState> {
  private _queryToken = 0;

  constructor(
    private readonly index: SearchIndexSource,
    private readonly repo: SearchRepository,
  ) {
    super({
      query: "",
      status: "idle",
      failed: false,
      exact: [],
      merchants: [],
    });
  }

  /** Run a query. Stale responses are discarded if a newer query arrives. */
  async setQuery(raw: string): Promise<void> {
    const token = ++this._queryToken;
    const intents = classifyQuery(raw);

    if (intents.length === 0) {
      this.setState({
        query: raw,
        status: "idle",
        failed: false,
        exact: [],
        merchants: [],
      });
      return;
    }

    this.setState({
      ...this.state,
      query: raw,
      status: "searching",
      failed: false,
    });

    const freeText = intents.find((i) => i.kind === "freeText");
    const probes = intents.filter((i) => i.kind !== "freeText");

    const [merchants, exact] = await Promise.all([
      freeText
        ? this._searchIndex(freeText.value)
        : Promise.resolve({ items: [], failed: false }),
      this._runProbes(probes),
    ]);

    // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
    if (token !== this._queryToken) return;

    // A record found live is the fresher copy of the same merchant, so drop
    // the index's duplicate rather than listing it twice.
    const exactIds = new Set(exact.items.map((item) => item.id));
    this.setState({
      query: raw,
      status: "ready",
      failed: merchants.failed || exact.failed,
      exact: exact.items,
      merchants: merchants.items.filter((item) => !exactIds.has(item.id)),
    });
  }

  /**
   * Loads on every query rather than caching the resolved artifact.
   *
   * The palette stays mounted for the whole session, so holding the first
   * result forever would hide every later build. The repository caches for the
   * build cadence, so repeat calls are free.
   */
  private async _searchIndex(query: string): Promise<ProbeOutcome> {
    let artifact: SearchIndexArtifact;
    try {
      artifact = await this.index.load();
    } catch (err) {
      // No index published yet is expected, and exact lookups still work.
      return { items: [], failed: !isMissingSearchIndex(err) };
    }
    return {
      items: searchDocs(artifact.docs, query)
        .slice(0, MAX_INDEX_RESULTS)
        .map(({ doc }) => ({
          id: doc.id,
          title: doc.businessName,
          subtitle: doc.emails[0],
          path: merchantPath(doc.id),
        })),
      failed: false,
    };
  }

  /**
   * Runs every candidate lookup at once and keeps whichever resolve.
   *
   * A UUID could name a merchant, a transaction or an invoice, and a probe
   * for the wrong one simply returns nothing — so a single failure must not
   * discard the probes that succeeded.
   */
  private async _runProbes(intents: SearchIntent[]): Promise<ProbeOutcome> {
    const settled = await Promise.allSettled(
      intents.map((intent) => this._probe(intent)),
    );
    return {
      items: settled
        .filter(
          (r): r is PromiseFulfilledResult<LiveMatch | null> =>
            r.status === "fulfilled",
        )
        .map((r) => r.value)
        .filter((match): match is LiveMatch => match !== null)
        .map((match) => ({
          id: match.id,
          title: match.title,
          subtitle: match.subtitle,
          path: pathFor(match),
        })),
      failed: settled.some((r) => r.status === "rejected"),
    };
  }

  private _probe(intent: SearchIntent): Promise<LiveMatch | null> {
    switch (intent.kind) {
      case "stripeAccount":
        return this.repo.findByStripeAccount(intent.value);
      case "stripeCustomer":
        return this.repo.findByStripeCustomer(intent.value);
      case "stripeSubscription":
        return this.repo.findByStripeSubscription(intent.value);
      case "stripeCharge":
        return this.repo.findByStripeCharge(intent.value);
      case "email":
        return this.repo.findByEmail(intent.value);
      case "businessId":
        return this.repo.findBusiness(intent.value);
      case "transactionId":
        return this.repo.findTransaction(intent.value);
      case "invoiceId":
        return this.repo.findInvoice(intent.value);
      case "freeText":
        return Promise.resolve(null);
    }
  }
}
